import { Dispatch, PayloadAction, createSlice } from '@reduxjs/toolkit'

import {
  BackgroundType,
  ProjectIdentity,
  RootStateTypeExtra,
  SessionListContent,
} from '@store/types'

import { ProjectType } from '@adUtilities/constants/common'

const STORAGE_KEY = 'projectIdentity'

const defaultConfig: ProjectIdentity = {
  disconnect: true,
  showClearCacheOption: true,
  projectId: '',
  projectName: '',
  projectLabel: '',
  projectUUID: '',
  sessionId: '',
  assetPort: '',
  assetHost: '',
  assetOptions: {
    localImages: false,
    localVideos: false,
  },
  sessionList: [],
  masterKey: '',
  disconnectedSessions: [],
  disableSoldProperty: false,
  fullScreen: true,
  lightUpBuildingModelOnFilter: false,
  prices: {
    max: [],
    min: [],
  },
  aspects: [],
  unitTypes: [],
  propertyConfig: {},
  houseAndLandConfig: {},
  gallerySettings: {
    intervalInSeconds: 7,
    type: 'slide',
  },

  hideFilter: false,
  hideInfoPanelAspect: false,
  hidePropertyInfoName: false,
  hideSoldPrice: false,
  idlePage: {
    active: false,
    page: '',
  },
  logo: {
    visible: false,
    url: '',
  },
  longhand: false,
  onlyShowPropertyGalleries: false,
  propertyGalleryControlStyle: 'thumbnail',
  propertyGalleryTab: {
    active: false,
    duration: 0,
  },
  propertyPrefix: '',
  showPrice: false,
  showcaseActive: false,
  sidePanelTitle: '',
  markerColourSettings: {
    available: '#FFFFFF',
    sold: '#D14417',
    area: '#FFFFFF',
    levels: '#FFFFFF',
  },
  statusLabels: {},
  statusColors: {},
  isSnaploaderActive: false,
  isEnvisionVRActive: false,
  hasThreeDViewer: false,
  theme: {
    font: '',
    mainColour: '',
    secondaryColour: '',
    neutralColour: '',
    tertiaryColour: '',
    remoteBackground: {
      type: BackgroundType.Color,
      color: '#0F172A',
      url: '',
    },
    remoteTertiaryColour: '',
    remoteVisionBackground: {
      type: BackgroundType.None,
      color: '',
      url: '',
    },
    remoteVisionLogo: {
      url: '',
    },
  },
  navigationSettings: [],
  remoteInteractiveBuilding: {
    areaView: {
      useMatrix: false,
    },
  },
  type: ProjectType.Apartment,
  showcase3DIntegration: false,
}

let initialState = defaultConfig as ProjectIdentity

const projectIdentity = localStorage.getItem('projectIdentity')

if (projectIdentity) {
  initialState = JSON.parse(projectIdentity)
}

export const saveProjectIdentityToStorage = (data: ProjectIdentity) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  return data
}

const projectIdentitySlice = createSlice({
  name: 'projectIdentity',
  initialState,
  reducers: {
    setProjectIdentity: (
      state: ProjectIdentity,
      action: PayloadAction<ProjectIdentity>
    ) => {
      const updatedState = saveProjectIdentityToStorage(action.payload)
      return {
        ...state,
        ...updatedState,
      }
    },
    setSessionList: (state, action: PayloadAction<SessionListContent[]>) => {
      const nextState: ProjectIdentity = {
        ...state,
        sessionList: action.payload,
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextState))
      return nextState
    },
    removeProjectIdentity: () => {
      localStorage.removeItem(STORAGE_KEY)
      return defaultConfig
    },
  },
})

export const setProjectIdentity =
  (payload: ProjectIdentity) =>
  (dispatch: Dispatch, getState: () => RootStateTypeExtra) => {
    const state = getState()

    if (state.appConfig.isProjectSwitchInitiated) {
      return
    }

    dispatch(projectIdentitySlice.actions.setProjectIdentity(payload))
  }

export const { removeProjectIdentity, setSessionList } =
  projectIdentitySlice.actions

export const selectProjectIdentity = (state: RootStateTypeExtra) =>
  state.projectIdentity

export default projectIdentitySlice.reducer
